import React, { useEffect, useState } from "react";
import { Chip, useTheme } from "@mui/material";
import { getSkillColors } from "../../helpers/color";
import sanitizeHtml from "sanitize-html";
import api from "../login/interceptors";
import { BASE_URL } from "../../Constants";
import { ErrorBox, OrgModal, PrimaryButton } from "../organisation/OrgModal";
import { getErrorMessage } from "../../utils/interviewApi";

export const ASSIGNED_VIEW = {
  CANDIDATE: "candidate",
  JOB: "job",
};

const formatYears = (value) => {
  const years = Number(value);
  if (value === null || value === undefined || value === "" || Number.isNaN(years)) return "0 years";
  const rounded = Math.round(years * 10) / 10;
  return `${rounded} ${rounded === 1 ? "year" : "years"}`;
};

// final_score is out of 100 elsewhere in the app and shown out of 10
const formatScore = (value) => {
  const score = Number(value);
  if (value === null || value === undefined || value === "" || Number.isNaN(score)) return null;
  return (score / 10).toFixed(1);
};

const SkillChips = ({ skills }) => {
  if (!skills.length) return <p className="text-sm text-accent-gray-light">None listed</p>;
  return (
    <ul className="flex flex-wrap gap-2">
      {skills.map((skill) => {
        const label = String(skill);
        const { bgColor, borderColor } = getSkillColors(label);
        return (
          <li key={label}>
            <Chip
              label={label}
              size="small"
              sx={{
                border: "1px solid",
                color: "rgba(0, 0, 0, 1)",
                backgroundColor: bgColor,
                borderRadius: "50px",
                borderColor,
                "& .MuiChip-label": { padding: "0 10px" },
              }}
            />
          </li>
        );
      })}
    </ul>
  );
};

const Field = ({ label, children }) => (
  <div className="flex items-center gap-2 text-sm">
    <dt className="font-semibold">{label}:</dt>
    <dd className="font-medium">{children}</dd>
  </div>
);

const SectionTitle = ({ children }) => <h3 className="font-semibold text-sm mb-2">{children}</h3>;

const CandidateDetails = ({ candidate }) => {
  const theme = useTheme();
  const score = formatScore(candidate.final_score);
  const skills = candidate.parsed_skills || [];
  const name = candidate.name || "Candidate";
  const initial = name.trim().charAt(0).toUpperCase() || "C";
  const cardStyle = { borderColor: theme.palette.secondary.v4, backgroundColor: theme.palette.primary.main };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4 p-4 rounded-lg border" style={cardStyle}>
        <div
          className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center text-white text-lg font-semibold"
          style={{ backgroundColor: theme.palette.info.main }}
          aria-hidden="true"
        >
          {initial}
        </div>
        <div className="min-w-0">
          <p className="text-lg font-semibold leading-tight">{name}</p>
          <p className="text-sm mt-1 text-accent-gray-light">{candidate.job_title || ""}</p>
          <div className="flex items-center gap-2">
            <p className="text-sm text-accent-gray-light">Experience</p>
            <p className="font-semibold">{formatYears(candidate.total_exp)}</p>
          </div>
          {score !== null && (
          <div className="flex items-center gap-2">
            <p className="text-sm text-accent-gray-light">Match score</p>
            <p className="font-semibold">{score}<span className=""> / 10</span></p>
          </div>
          )}
        </div>
      </div>

      {score !== null && (
        <div className={`grid gap-3 ${score !== null ? "grid-cols-2" : "grid-cols-1"}`}>
            <div className="rounded-lg border px-4 py-3" style={cardStyle}>
              <p className="text-sm text-accent-gray-light">Match score</p>
              <p className="text-lg font-semibold mt-1">
                {score}
                <span className="text-sm font-normal text-accent-gray-light"> / 10</span>
              </p>
            </div>
        </div>
      )}

      <div className="rounded-lg border px-4 py-3" style={cardStyle}>
        <SectionTitle>Skills</SectionTitle>
        <SkillChips skills={skills} />
      </div>

      {candidate.original_resume_url && (
        <PrimaryButton
          component="a"
          href={candidate.original_resume_url}
          target="_blank"
          rel="noopener noreferrer"
          className="self-start"
        >
          Open resume
        </PrimaryButton>
      )}
    </div>
  );
};

const JobDetails = ({ job }) => {
  const theme = useTheme();
  const description = sanitizeHtml(String(job.job_description || "").replace(/\n/g, "<br/>"));
  return (
    <>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Title">{job.title || "—"}</Field>
        <Field label="Minimum experience">{formatYears(job.jd_min_exp)}</Field>
      </dl>
      <div>
        <SectionTitle>Mandatory skills:</SectionTitle>
        <SkillChips skills={job.mandatory_skills || []} />
      </div>
      <div>
        <SectionTitle>Preferred skills:</SectionTitle>
        <SkillChips skills={job.preferred_skills || []} />
      </div>
      <div>
        <SectionTitle>Description:</SectionTitle>
        {description ? (
          <div
            className="text-sm leading-6 p-3 rounded border max-h-80 overflow-y-auto"
            style={{ borderColor: theme.palette.secondary.v4 }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        ) : (
          <p className="text-sm text-accent-gray-light">No description</p>
        )}
      </div>
    </>
  );
};

// Read-only view of the candidate or job behind an interview the panelist is assigned to (spec 3.2).
// The backend only returns these for interviews the panelist is on.
const AssignedInterviewDetailsModal = ({ interview, view, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isOpen = Boolean(interview && view);
  const candidateId = interview?.candidate?.candidate_id || interview?.candidate_id;
  const jobId = interview?.jd_uuid;

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setData(null);
    setError("");
    setLoading(true);
    const request =
      view === ASSIGNED_VIEW.CANDIDATE
        ? api
            .get(`${BASE_URL}/api/single-candidate/${candidateId}`, { params: { jd_id: jobId } })
            .then((response) => response.data?.candidate || response.data || {})
        : api.get(`${BASE_URL}/api/job-desc/${jobId}`).then((response) => {
            const body = response.data;
            return (Array.isArray(body) ? body[0] : body) || {};
          });
    request
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getErrorMessage(err, view === ASSIGNED_VIEW.CANDIDATE ? "Could not load the candidate." : "Could not load the job description."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, view, candidateId, jobId]);

  const isCandidate = view === ASSIGNED_VIEW.CANDIDATE;
  const context = [interview?.jd_title, interview?.round_name].filter(Boolean).join(" · ");

  return (
    <OrgModal
      isOpen={isOpen}
      onClose={onClose}
      titleId="assigned-interview-details-title"
      title={isCandidate ? data?.name || interview?.candidate?.name || "Candidate" : "Job Description"}
      maxWidth="max-w-2xl"
    >
      {loading ? (
        <p className="text-sm">Loading...</p>
      ) : error ? (
        <ErrorBox>{error}</ErrorBox>
      ) : data ? (
        isCandidate ? <CandidateDetails candidate={data} /> : <JobDetails job={data} />
      ) : null}
    </OrgModal>
  );
};

export default AssignedInterviewDetailsModal;
